
import React, { createContext, useContext, useMemo, useState } from 'react';
import { apiURL, serverURL } from 'config';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { setLoading } from 'redux/actions/app';
import axios from 'axios';
import moment from 'moment';
import { store } from 'react-notifications-component';
import * as API from 'services/api';
import { RemoveModal } from 'components';
import FileUploadModal from 'components/FileUploadModal';
import { encodeGedId, getUserIdFromToken, getRoleFromToken } from 'utils';
import { appProfileSelector } from 'redux/selectors';
import { authSelector } from 'redux/selectors';

export default function useHomeContext() {
    const context = useContext(HomeContext);
    if (!context) {
        throw new Error('useHomeContext must be used within a HomeProvider');
    }
    return context;
}

const HomeContext = createContext();

export const HomeProvider = ({ children }) => {
    const dispatch = useDispatch();
    const profile = useSelector(appProfileSelector);
    const { accessToken, refreshToken, email, tokenType } = useSelector(authSelector);

    // Note: getUserIdFromToken and getRoleFromToken are imported from utils

    // Helper function to build secure document URL
    // Format: /documents/Documents?id={encodedGed}&u={userId}&token={token}
    const buildDocumentUrl = (ged) => {
        if (!ged) return '';
        
        // Always use the current token from Redux (never use cached/stale tokens)
        if (!accessToken) {
            console.warn('No access token available. User may need to log in again.');
            return '';
        }
        
        // Try to get userId from profile first, then from token
        let userId = profile?.id || '';
        if (!userId && accessToken) {
            userId = getUserIdFromToken(accessToken);
        }
        
        // Encode userId and accessToken with base64
        const encodedUserId = btoa(unescape(encodeURIComponent(userId)));
        const encodedAccessToken = btoa(unescape(encodeURIComponent(accessToken)));
        
        // Use simplified URL format: /document?id={encodedGed}&u={userId}&token={token}
        // Note: Using serverURL directly instead of apiURL to avoid /v1 prefix
        return `${serverURL}assets/${encodeURIComponent(ged)}/${encodeURIComponent(encodedUserId)}/${encodeURIComponent(encodedAccessToken)}`;
    };

    const onDirectDownload = ({ ged, title, documentDate, productId, type }) => async e => {
        dispatch(setLoading(true));
        const url = buildDocumentUrl(ged);
        await axios({ url, method: 'GET', responseType: 'blob' })
            .then((response) => {
                dispatch(setLoading(false));
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                const filename = productId ? `${type}_${moment(documentDate).format('DDMMYYYY')}_${productId}.pdf` : `${title}.pdf`;
                link.setAttribute('download', productId ? `${type}_${moment(documentDate).format('DDMMYYYY')}_${productId}.pdf` : `${title}.pdf`);
                document.body.appendChild(link);
                link.click();
                window.dataLayer.push({
                    'event': 'download',
                    'file_name': filename,
                    'file_type': 'PDF',
                });
            }).catch(err => {
                console.log(err)
                dispatch(setLoading(false));
                window.open(buildDocumentUrl(ged), "_blank");
            });
    }

    const onDownload = (ged) => e => {
        if (ged) {
            const url = buildDocumentUrl(ged);
            if (!accessToken || !tokenType) {
                console.warn('No access token available. User may need to log in again.');
                window.location.href = url;
                return;
            }
            
            const authToken = `${tokenType} ${accessToken}`;
            
            // Add timestamp to URL to bypass cache and avoid 304 issues
            const urlWithTimestamp = `${url}${url.includes('?') ? '&' : '?'}_t=${Date.now()}`;
            
            // Make a request with custom headers first, bypass cache to avoid 304 issues
            fetch(urlWithTimestamp, {
                method: 'GET',
                headers: {
                    'Authorization': authToken,
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache'
                },
                cache: 'no-store'
            })
            .then(response => {
                // Check response status
                if (response.status === 304) {
                    // If 304, make a fresh request without cache
                    return fetch(urlWithTimestamp, {
                        method: 'GET',
                        headers: {
                            'Authorization': authToken,
                            'Cache-Control': 'no-cache, no-store, must-revalidate',
                            'Pragma': 'no-cache',
                            'If-None-Match': '',
                            'If-Modified-Since': ''
                        },
                        cache: 'reload'
                    }).then(res => res.blob());
                }
                if (!response.ok && response.status !== 304) {
                    throw new Error(`Network response was not ok: ${response.status}`);
                }
                return response.blob();
            })
            .then(blob => {
                // Validate blob
                if (!blob || blob.size === 0) {
                    throw new Error('Received empty blob');
                }
                
                // Check if it's a PDF
                if (blob.type && !blob.type.includes('pdf') && !blob.type.includes('application/octet-stream')) {
                    console.warn('Unexpected blob type:', blob.type);
                }
                
                // Create blob URL for the PDF
                const blobUrl = window.URL.createObjectURL(blob);
                
                // Update the browser URL to keep the server URL visible
                window.history.pushState({}, '', url);
                
                // Create a full-screen container to display the PDF in current window
                const container = document.createElement('div');
                container.style.position = 'fixed';
                container.style.top = '0';
                container.style.left = '0';
                container.style.width = '100%';
                container.style.height = '100%';
                container.style.zIndex = '9999';
                container.style.backgroundColor = '#fff';
                
                // Use embed tag for PDF (works better than iframe for PDFs)
                const embed = document.createElement('embed');
                embed.style.width = '100%';
                embed.style.height = '100%';
                embed.style.border = 'none';
                embed.src = blobUrl;
                embed.type = 'application/pdf';
                
                // Fallback to iframe if embed doesn't work
                const iframe = document.createElement('iframe');
                iframe.style.position = 'absolute';
                iframe.style.top = '0';
                iframe.style.left = '0';
                iframe.style.width = '100%';
                iframe.style.height = '100%';
                iframe.style.border = 'none';
                iframe.src = blobUrl;
                iframe.style.display = 'none'; // Hide initially
                
                // Try embed first, fallback to iframe
                embed.onerror = () => {
                    console.warn('Embed failed, trying iframe');
                    embed.style.display = 'none';
                    iframe.style.display = 'block';
                };
                
                iframe.onerror = () => {
                    console.error('Error loading PDF');
                    cleanup();
                    window.location.href = url;
                };
                
                iframe.onload = () => {
                    console.log('PDF loaded successfully');
                };
                
                container.appendChild(embed);
                container.appendChild(iframe);
                
                // Add close button
                const closeBtn = document.createElement('button');
                closeBtn.innerHTML = '×';
                closeBtn.style.position = 'fixed';
                closeBtn.style.top = '10px';
                closeBtn.style.right = '10px';
                closeBtn.style.zIndex = '10000';
                closeBtn.style.width = '40px';
                closeBtn.style.height = '40px';
                closeBtn.style.borderRadius = '50%';
                closeBtn.style.border = 'none';
                closeBtn.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
                closeBtn.style.color = 'white';
                closeBtn.style.fontSize = '24px';
                closeBtn.style.cursor = 'pointer';
                closeBtn.style.display = 'flex';
                closeBtn.style.alignItems = 'center';
                closeBtn.style.justifyContent = 'center';
                
                const cleanup = () => {
                    if (container && container.parentNode) {
                        document.body.removeChild(container);
                    }
                    if (closeBtn && closeBtn.parentNode) {
                        document.body.removeChild(closeBtn);
                    }
                    if (blobUrl) {
                        window.URL.revokeObjectURL(blobUrl);
                    }
                    // Restore previous URL
                    // window.history.back();
                };
                
                closeBtn.onclick = cleanup;
                
                // Add to page
                document.body.appendChild(container);
                document.body.appendChild(closeBtn);
                
                // Clean up on browser back button
                window.addEventListener('popstate', cleanup, { once: true });
            })
            .catch(error => {
                console.error('Error:', error);
                // Fallback to direct URL navigation if fetch fails
                window.location.href = url;
            });
        } else {
            window.open(buildDocumentUrl(ged), "_blank");
        }
    }

    const onDiscoverFile = (ged, link) => {
        if (ged) {
            const url = buildDocumentUrl(ged);
            if (!accessToken || !tokenType) {
                console.warn('No access token available. User may need to log in again.');
                window.location.href = url;
                return;
            }
            
            const authToken = `${tokenType} ${accessToken}`;
            
            // Add timestamp to URL to bypass cache and avoid 304 issues
            const urlWithTimestamp = `${url}${url.includes('?') ? '&' : '?'}_t=${Date.now()}`;
            
            // Make a request with custom headers first, bypass cache to avoid 304 issues
            fetch(urlWithTimestamp, {
                method: 'GET',
                headers: {
                    'Authorization': authToken,
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache'
                },
                cache: 'no-store'
            })
            .then(response => {
                // Check response status
                if (response.status === 304) {
                    // If 304, make a fresh request without cache
                    return fetch(urlWithTimestamp, {
                        method: 'GET',
                        headers: {
                            'Authorization': authToken,
                            'Cache-Control': 'no-cache, no-store, must-revalidate',
                            'Pragma': 'no-cache',
                            'If-None-Match': '',
                            'If-Modified-Since': ''
                        },
                        cache: 'reload'
                    }).then(res => res.blob());
                }
                if (!response.ok && response.status !== 304) {
                    throw new Error(`Network response was not ok: ${response.status}`);
                }
                return response.blob();
            })
            .then(blob => {
                // Validate blob
                if (!blob || blob.size === 0) {
                    throw new Error('Received empty blob');
                }
                
                // Check if it's a PDF
                if (blob.type && !blob.type.includes('pdf') && !blob.type.includes('application/octet-stream')) {
                    console.warn('Unexpected blob type:', blob.type);
                }
                
                // Create blob URL for the PDF
                const blobUrl = window.URL.createObjectURL(blob);
                
                // Update the browser URL to keep the server URL visible
                window.history.pushState({}, '', url);
                
                // Create a full-screen container to display the PDF in current window
                const container = document.createElement('div');
                container.style.position = 'fixed';
                container.style.top = '0';
                container.style.left = '0';
                container.style.width = '100%';
                container.style.height = '100%';
                container.style.zIndex = '9999';
                container.style.backgroundColor = '#fff';
                
                // Use embed tag for PDF (works better than iframe for PDFs)
                const embed = document.createElement('embed');
                embed.style.width = '100%';
                embed.style.height = '100%';
                embed.style.border = 'none';
                embed.src = blobUrl;
                embed.type = 'application/pdf';
                
                // Fallback to iframe if embed doesn't work
                const iframe = document.createElement('iframe');
                iframe.style.position = 'absolute';
                iframe.style.top = '0';
                iframe.style.left = '0';
                iframe.style.width = '100%';
                iframe.style.height = '100%';
                iframe.style.border = 'none';
                iframe.src = blobUrl;
                iframe.style.display = 'none'; // Hide initially
                
                // Try embed first, fallback to iframe
                embed.onerror = () => {
                    console.warn('Embed failed, trying iframe');
                    embed.style.display = 'none';
                    iframe.style.display = 'block';
                };
                
                iframe.onerror = () => {
                    console.error('Error loading PDF');
                    cleanup();
                    window.location.href = url;
                };
                
                iframe.onload = () => {
                    console.log('PDF loaded successfully');
                };
                
                container.appendChild(embed);
                container.appendChild(iframe);
                
                // Add close button
                const closeBtn = document.createElement('button');
                closeBtn.innerHTML = '×';
                closeBtn.style.position = 'fixed';
                closeBtn.style.top = '10px';
                closeBtn.style.right = '10px';
                closeBtn.style.zIndex = '10000';
                closeBtn.style.width = '40px';
                closeBtn.style.height = '40px';
                closeBtn.style.borderRadius = '50%';
                closeBtn.style.border = 'none';
                closeBtn.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
                closeBtn.style.color = 'white';
                closeBtn.style.fontSize = '24px';
                closeBtn.style.cursor = 'pointer';
                closeBtn.style.display = 'flex';
                closeBtn.style.alignItems = 'center';
                closeBtn.style.justifyContent = 'center';
                
                const cleanup = () => {
                    if (container && container.parentNode) {
                        document.body.removeChild(container);
                    }
                    if (closeBtn && closeBtn.parentNode) {
                        document.body.removeChild(closeBtn);
                    }
                    if (blobUrl) {
                        window.URL.revokeObjectURL(blobUrl);
                    }
                    // Restore previous URL
                    // window.history.back();
                };
                
                closeBtn.onclick = cleanup;
                
                // Add to page
                document.body.appendChild(container);
                document.body.appendChild(closeBtn);
                
                // Clean up on browser back button
                window.addEventListener('popstate', cleanup, { once: true });
            })
            .catch(error => {
                console.error('Error:', error);
                // Fallback to direct URL navigation if fetch fails
                window.location.href = url;
            });
        } else if (link) {
            window.open(link, "_blank");
        }
    }

    const onDownloadFile = async file => {
        const url = file.ged ? buildDocumentUrl(file.ged) : file.privateUrl;
        let filename = file.title + ".pdf"
        if (!file.ged) {
            const splitnames = file.Key.split("/")
            filename = splitnames[splitnames.length - 1]
        }
        dispatch(setLoading(true));
        await axios({ url: url, method: 'GET', responseType: 'blob' })
            .then((response) => {
                dispatch(setLoading(false));
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
            }).catch(err => {
                console.log(err)
                dispatch(setLoading(false));
                window.open(url);
            });
    };

    const [importUsersModalVisible, setImportUsersModalVisible] = useState(false);

    const [duplicated, setDuplicated] = useState();

    const toggleUsersImportModal = () => {
        setImportUsersModalVisible(false);
    };
    const onImportTrainees = (file, type, forced) => {
        setDuplicated();
        dispatch(setLoading(true));
        const formData = new FormData();
        formData.append('type', type);
        formData.append('file', file);
        if (forced)
            formData.append('forced', forced);

        API.importTraines(formData)
            .then(res => {
                dispatch(setLoading(false));
                if (res.code === 201) {
                    setDuplicated({ file, type });
                } else if (res.code === 200) {
                    window.dataLayer.push({
                        'event': 'action',
                        'click_name': 'IMPORTER FORMATION AUTRE ORGANISME',
                        'action_type': 'IMPORT',
                    });
                    toggleUsersImportModal();
                    store.addNotification({
                        message: "uploaded successfully.",
                        type: 'success',
                        container: 'top-right',
                        animationIn: ['animated', 'fadeIn'],
                        animationOut: ['animated', 'fadeOut'],
                        dismiss: {
                            duration: 5000,
                            onScreen: true,
                        },
                    });
                } else {
                    store.addNotification({
                        message: res.message,
                        type: 'danger',
                        container: 'top-right',
                        animationIn: ['animated', 'fadeIn'],
                        animationOut: ['animated', 'fadeOut'],
                        dismiss: {
                            duration: 5000,
                            onScreen: true,
                        },
                    });
                }
            })
            .catch(err => {
                console.log(err)
                dispatch(setLoading(false));
            });
    };

    const values = useMemo(() => ({
        onDirectDownload,
        onDownload,
        onDiscoverFile,
        onDownloadFile,

        setImportUsersModalVisible
    }), [
        accessToken,
        tokenType,
        profile
    ]);
    return (
        <HomeContext.Provider
            value={values}
        >
            {children}
            <FileUploadModal
                addFree={true}
                visible={importUsersModalVisible}
                onConfirm={onImportTrainees}
                onClose={toggleUsersImportModal}
            />
            {duplicated && (
                <RemoveModal
                    visible={!!duplicated}
                    onClose={() => setDuplicated()}
                    onConfirm={() => {
                        onImportTrainees(duplicated.file, duplicated.type, true)
                    }}
                    loading={false}
                    title="Confirm"
                    description={"Cette session semble avoir déjà été importée, souhaitez vous continuer ?"}
                />
            )}
        </HomeContext.Provider>
    );
};

HomeProvider.propTypes = {
    children: PropTypes.any,
};